// src/App.js
import React, { useState, createContext, useContext, useEffect } from 'react';
import './App.css';
import AppHeader from './components/Header';
import AppFooter from './components/FooterItem';
import NotFound from './pages/NotFound';
import WriteArticle from './pages/WritePost';
import About from './pages/About';
import Archive from './pages/Archive';
import PostDetail from './pages/PostDetail';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Auth from './pages/Auth';

const PostContext = createContext();
export const usePostContext = () => useContext(PostContext);

const PrivateRoute = ({ loggedInUser, children, requiredRole }) => {
  if (!loggedInUser) {
    return <Navigate to="/login" replace />;
  }
  
  // 检查角色权限
  if (requiredRole && requiredRole !== loggedInUser.role) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const [loggedInUser, setLoggedInUser] = useState(null);
  // 文章数据持久化（从localStorage读取，关联用户名）
  const [posts, setPosts] = useState(() => {
    const savedPosts = localStorage.getItem('blogPosts');
    return savedPosts ? JSON.parse(savedPosts) : [];
  });
  // 注册用户数据持久化
  const [registeredUsers, setRegisteredUsers] = useState(() => {
    const savedUsers = localStorage.getItem('registeredUsers');
    return savedUsers ? JSON.parse(savedUsers) : [
      { username: 'admin', password: '123456', role: 'admin', joinDate: '2024-01-01' },
      { username: 'user', password: '123456', role: 'user', joinDate: '2024-01-01' }
    ];
  });

  // 页面加载时恢复登录状态
  useEffect(() => {
    const savedUser = localStorage.getItem('loggedInUser');
    if (savedUser) {
      setLoggedInUser(JSON.parse(savedUser));
    }
  }, []);

  // 文章数据变化时，同步到localStorage（持久化）
  useEffect(() => {
    localStorage.setItem('blogPosts', JSON.stringify(posts));
  }, [posts]);

  // 注册用户变化时，同步到localStorage（持久化）
  useEffect(() => {
    localStorage.setItem('registeredUsers', JSON.stringify(registeredUsers));
  }, [registeredUsers]);

  // 添加文章：关联当前登录用户的用户名
  const addPostToArchive = (newPost) => {
    const postWithMeta = {
      ...newPost,
      id: Date.now().toString(),
      publishTime: new Date().toLocaleString(),
      author: loggedInUser?.username || '匿名用户', // 关联作者（当前登录用户）
      views: 0,
      viewUsers: [], // 记录浏览过文章的用户
      likeCount: 0,
      likedBy: [] // 记录点赞的用户
    };
    setPosts([postWithMeta, ...posts]);
  };

  // 编辑文章
  const editPost = (postId, updatedPost) => {
    setPosts(prevPosts => 
      prevPosts.map(post => 
        post.id === postId ? { ...post, ...updatedPost, lastModified: new Date().toLocaleString() } : post
      )
    );
  };

  // 增加文章浏览量（防止重复浏览）
  const incrementPostViews = (postId) => {
    if (!loggedInUser) return; // 未登录用户不增加浏览量
    
    setPosts(prevPosts => 
      prevPosts.map(post => {
        if (post.id === postId) {
          // 检查用户是否已经浏览过此文章
          const hasViewed = post.viewUsers?.includes(loggedInUser.username);
          
          if (!hasViewed) {
            // 如果未浏览过，则增加浏览量并记录用户
            return {
              ...post,
              views: post.views + 1,
              viewUsers: [...(post.viewUsers || []), loggedInUser.username]
            };
          }
        }
        return post;
      })
    );
  };

  // 点赞/取消点赞文章
  const togglePostLike = (postId) => {
    if (!loggedInUser) return; // 未登录用户无法点赞
    
    setPosts(prevPosts => 
      prevPosts.map(post => {
        if (post.id === postId) {
          const isLiked = post.likedBy?.includes(loggedInUser?.username);
          const newLikedBy = isLiked 
            ? post.likedBy.filter(user => user !== loggedInUser?.username)
            : [...(post.likedBy || []), loggedInUser?.username];
          
          return {
            ...post,
            likeCount: isLiked ? post.likeCount - 1 : post.likeCount + 1,
            likedBy: newLikedBy
          };
        }
        return post;
      })
    );
  };

  // 检查用户是否已点赞某文章
  const isPostLikedByUser = (postId) => {
    const post = posts.find(p => p.id === postId);
    return post?.likedBy?.includes(loggedInUser?.username) || false;
  };

  // 检查用户是否已浏览某文章
  const hasUserViewedPost = (postId) => {
    const post = posts.find(p => p.id === postId);
    return post?.viewUsers?.includes(loggedInUser?.username) || false;
  };

  const handleLogout = () => {
    setLoggedInUser(null);
    localStorage.removeItem('loggedInUser'); // 清除登录状态
    console.log('登出成功');
  };

  const handleLogin = (userInfo) => {
    // 如果用户没有加入日期，则设置为当前日期
    const userWithJoinDate = {
      ...userInfo,
      joinDate: userInfo.joinDate || new Date().toISOString().split('T')[0]
    };
    
    setLoggedInUser(userWithJoinDate);
    localStorage.setItem('loggedInUser', JSON.stringify(userWithJoinDate));
  };

  const handleRegister = (userInfo) => {
    const now = new Date().toISOString().split('T')[0]; // 获取当前日期
    const newUser = { 
      username: userInfo.username, 
      password: userInfo.password,
      role: 'user', // 默认为普通用户
      joinDate: now // 设置注册日期
    };
    const newUsers = [...registeredUsers, newUser];
    setRegisteredUsers(newUsers);
  };

  return (
    <div className="App">
      <PostContext.Provider 
        value={{ 
          posts, 
          setPosts,
          addPostToArchive, 
          editPost,
          incrementPostViews,
          togglePostLike,
          isPostLikedByUser,
          hasUserViewedPost,
          loggedInUser, 
          handleLogin,  
          handleLogout,
          registeredUsers, 
          handleRegister  
        }}
      >
        <Router>
          <AppHeader loggedInUser={loggedInUser} onLogout={handleLogout} />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/archive" element={<Archive />} />
            <Route path="/login" element={<Auth />} />
            <Route 
              path="/write" 
              element={
                <PrivateRoute loggedInUser={loggedInUser}>
                  <WriteArticle />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <PrivateRoute loggedInUser={loggedInUser} requiredRole="admin">
                  <div>管理员页面</div>
                </PrivateRoute>
              } 
            />
            <Route path="/my" element={<About />} />
            <Route path="/postdetail/:id" element={<PostDetail />} />
            <Route path="*" element={<NotFound loggedInUser={loggedInUser} onLogout={handleLogout} />} />
          </Routes>
          <AppFooter />
        </Router>
      </PostContext.Provider>
    </div>
  );
}

export default App;




